/**
 * Data model for the Overall dashboard's "Bank Balance" chart (dashboards
 * round 3): monthly cash balance split UK / France / Personal, same
 * jurisdiction bucketing the Bank Balance stat card uses, so the latest
 * populated month's total reconciles to that card.
 *
 * Calendar-year axis (round 3): fixed Jan-Dec of the current year, derived
 * from `new Date()`, never hardcoded. Months with no snapshot/reconstructed
 * history (including every month after the current one) get `_total: null`
 * so the chart renders a gap rather than a fabricated zero -- this chart has
 * no forecast of its own (round 3 retires the forecast-continuation stack;
 * see buildJurisdictionCashForecastRows below for the Net Assets chart's own,
 * independent, forecast source, which still needs the raw forecast months).
 */

import { monthKeyToLabel, calendarYearMonthKeys } from './calendarYear';

export { calendarYearMonthKeys };

export var CASH_DEV_BUCKETS = [
  { key: 'UK', label: 'UK', color: '#2563EB' },
  { key: 'FR', label: 'France', color: '#DC2626' },
  { key: 'personal', label: 'Personal', color: '#F59E0B' },
];

function bucketValue(monthRow, bucketKey, ccyKey, eurGbpRate) {
  var bucket = monthRow[bucketKey];
  if (!bucket) return 0;
  var v = bucket[ccyKey];
  if (v != null) return v;
  // Forecast buckets carry eur:null; derive from gbp via the spot rate.
  if (ccyKey === 'eur' && bucket.gbp != null && eurGbpRate) return bucket.gbp / eurGbpRate;
  return 0;
}

/** Key used on a chart row for bucket `label`'s partial-coverage flag. */
export function partialFlagKey(label) {
  return '_partial_' + label;
}

/** Key used on a chart row for bucket `label`'s excluded-account list (human-readable labels, for tooltip display). */
export function excludedAccountsKey(label) {
  return '_excludedAccounts_' + label;
}

/**
 * @param {object} input
 * @param {object[]} input.months - GET /reports/group/cash-development "months" array
 * @param {string} [input.displayCurrency]
 * @param {number} [input.eurGbpRate]
 * @param {number} [input.year] - defaults to the current calendar year
 */
export function buildCashDevelopmentChartData(input) {
  var months = input.months || [];
  var displayCurrency = input.displayCurrency || 'GBP';
  var eurGbpRate = input.eurGbpRate || 0.855;
  var year = input.year || new Date().getFullYear();
  var ccyKey = displayCurrency === 'GBP' ? 'gbp' : 'eur';

  var byMonth = {};
  months.forEach(function (m) { byMonth[m.month] = m; });

  var rows = calendarYearMonthKeys(year).map(function (monthKey) {
    var m = byMonth[monthKey];
    var row = { month: monthKeyToLabel(monthKey), _monthKey: monthKey };
    if (!m) {
      CASH_DEV_BUCKETS.forEach(function (b) {
        row[b.label] = null;
        row[partialFlagKey(b.label)] = false;
        row[excludedAccountsKey(b.label)] = [];
      });
      row._unassigned = null;
      row._total = null;
      return row;
    }
    var total = 0;
    CASH_DEV_BUCKETS.forEach(function (b) {
      var v = bucketValue(m, b.key, ccyKey, eurGbpRate);
      row[b.label] = v;
      // A month this jurisdiction bucket has NO doc for at all (m[b.key] is
      // undefined) is a plain gap, not a partial figure -- only mark
      // partial when a doc exists and explicitly says some contributing
      // account was excluded (services/cash_jurisdiction_snapshots.py).
      var bucketDoc = m[b.key];
      row[partialFlagKey(b.label)] = !!bucketDoc && bucketDoc.coverage === 'partial';
      // Prefer human-readable labels (routes/cash_jurisdiction.py resolves
      // each excluded account_id to "Entity – Bank"); fall back to the raw
      // ids if a response predates that resolution, so the tooltip degrades
      // gracefully rather than showing nothing.
      row[excludedAccountsKey(b.label)] = (bucketDoc && (bucketDoc.excluded_account_labels || bucketDoc.excluded_accounts)) || [];
      total += v;
    });
    row._unassigned = bucketValue(m, 'unassigned', ccyKey, eurGbpRate);
    row._total = total + row._unassigned;
    return row;
  });

  return { actual: rows };
}

/** Latest populated month's total (UK + France + Personal + Unassigned), or null if no month has data. */
export function latestActualTotal(chartData) {
  var actual = chartData.actual || [];
  for (var i = actual.length - 1; i >= 0; i--) {
    if (actual[i]._total != null) return actual[i]._total;
  }
  return null;
}

/**
 * Net Assets chart's 3-month cash-forecast continuation for the group scope
 * (dashboards round 2, part 1b), independent of the (now forecast-less)
 * Bank Balance chart above. Reads the SAME raw forecast_months the Bank
 * Balance chart used to render (GET /reports/group/cash-development's own
 * `forecast_months`, re-bucketed by jurisdiction server-side) -- never a new
 * derivation, just no longer routed through buildCashDevelopmentChartData.
 */
export function buildJurisdictionCashForecastRows(forecastMonths, displayCurrency, eurGbpRate) {
  var ccyKey = (displayCurrency || 'GBP') === 'GBP' ? 'gbp' : 'eur';
  return (forecastMonths || []).map(function (m) {
    var total = CASH_DEV_BUCKETS.reduce(function (s, b) {
      return s + bucketValue(m, b.key, ccyKey, eurGbpRate);
    }, 0);
    return { month: monthKeyToLabel(m.month), _monthKey: m.month, _isForecast: true, _forecastTotal: total };
  });
}
